import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useUserAccess,
  useSnackbar,
  useStore,
  useMasterData,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowTab,
  ArrowTabGroup,
  CustomTabPanel,
  IconLibrary,
  Layout,
  StackablePageButtons,
} from '@product-framework/index';
import { FORM_VIEW } from '@product-framework/pages/ServiceProvider/constants';
import { ServiceModuleProvider } from '@product-framework/components/ServiceModuleProvider';
import FooterNavigation from '../../components/common/FooterNavigation';
import StepperTabsConfig from '../../components/StepperTabsConfig';
import ProfileCompleteness from '../../components/common/ProfileCompleteness';
import { ServiceProviderScopeProvider } from '../../scope';

function ServiceProviderEdit() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {
    id: pageId,
    tabNumber,
    action = FORM_VIEW.LIST,
    entityId = null,
  } = useParams();
  const userAccess = useUserAccess();
  const masterData = useMasterData();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);
  const store = useStore();

  const stepperTabs = useMemo(
    () => StepperTabsConfig(userAccess, masterData?.applicationFeatureDemo),
    [userAccess],
  );

  const spUrl = `${getApiUrl()}/sxcl/v1/service-providers/${pageId}`;

  const urlTabId = parseInt(tabNumber, 10) || 1;

  const [tabValue, setTabValue] = useState(
    urlTabId - 1 < stepperTabs.length ? urlTabId - 1 : 0,
  );

  const [tabErrors, setTabErrors] = useState({});

  const fetchSPProfileStatus = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${pageId}/profile-status`;
    const response = await get(url);
    if (response?.ok) {
      return response.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEPROVIDERS_BREADCRUMB',
        defaultMessage: 'Service Providers',
      }),
      icon: IconLibrary.ServiceProviders,
      onClick: () => {
        navigate('/serviceproviders');
      },
      permissionName: 'CAN_LIST_SP',
    },
    {
      name: formatMessage({
        id: 'EDIT_SERVICEPROVIDER_BREADCRUMB',
        defaultMessage: 'Edit Service Provider',
      }),
      icon: IconLibrary.ServiceProvider,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_SERVICEPROVIDERS_STACKABLE_BUTTON',
        defaultMessage: 'Create Service Provider',
      }),
      Icon: IconLibrary.ServiceProvider,
      permissionName: 'CAN_CREATE_SP',
      variant: 'outlined',
      onClick: () => navigate('/serviceproviders/create'),
    },
    {
      label: formatMessage({
        id: 'EDIT_SERVICEPROVIDERS_STACKABLE_BUTTON',
        defaultMessage: 'List Service Providers',
      }),
      permissionName: 'CAN_LIST_SP',
      Icon: IconLibrary.ServiceProviders,
      variant: 'outlined',
      onClick: () => navigate('/serviceproviders'),
    },
  ];

  const onMainTabChange = useCallback(
    newValue => {
      if (newValue !== 3) {
        store.domain.list.reset('ServiceProviderLocationList');
      }
      navigate(`/serviceproviders/edit/${pageId}/${newValue + 1}`);
    },
    [navigate, pageId, store],
  );

  useEffect(() => {
    setTabValue(urlTabId - 1 < stepperTabs.length ? urlTabId - 1 : 0);
  }, [urlTabId, stepperTabs.length]);

  const checkAndSetSPProfile = async () => {
    const status = await fetchSPProfileStatus();
    setTabErrors(status);
  };

  useEffect(() => {
    checkAndSetSPProfile();
  }, []);

  const ActiveStepper = stepperTabs[tabValue];

  const tabs = useMemo(
    () =>
      stepperTabs.map(stepper => (
        <ArrowTab
          key={stepper.label}
          maxTabs={stepperTabs.length}
          label={stepper.label}
          errorCount={stepper.getErrorCount?.(tabErrors) ?? null}
        />
      )),
    [stepperTabs, tabErrors],
  );

  return (
    <Layout>
      <ServiceProviderScopeProvider
        serviceProviderId={pageId}
        serviceProviderLocationId={entityId}
        mainTabIndex={tabValue}>
        <ServiceModuleProvider>
          <Grid2 xs={12} container>
            <Grid2 xs={12} md={6}>
              <Breadcrumb apiUrl={spUrl} nameKey="name" items={breadCrumb} />
            </Grid2>
            <Grid2 xs={12} md={6}>
              <StackablePageButtons buttons={stackablePageButtons} />
            </Grid2>
          </Grid2>
          <Grid2 xs={12}>
            <Box>
              <ProfileCompleteness
                store={store}
                serviceProviderId={pageId}
                status={tabErrors}
              />
            </Box>
            <Box>
              <ArrowTabGroup
                value={tabValue}
                onChange={(event, newValue) => {
                  onMainTabChange(newValue);
                }}>
                {tabs}
              </ArrowTabGroup>
            </Box>
          </Grid2>
          <Grid2 xs={12}>
            <CustomTabPanel value={tabValue} index={tabValue}>
              <ActiveStepper.Component
                setTabValue={setTabValue}
                serviceProviderId={pageId}
                currentIndex={tabValue}
                stepperTabs={stepperTabs}
                tabErrors={tabErrors}
                onChange={() => {
                  checkAndSetSPProfile();
                }}
                action={action}
                entityId={entityId}
              />
              <FooterNavigation
                onChange={newValue => {
                  onMainTabChange(newValue);
                }}
                currentIndex={tabValue}
                stepperTabs={stepperTabs}
              />
            </CustomTabPanel>
          </Grid2>
        </ServiceModuleProvider>
      </ServiceProviderScopeProvider>
    </Layout>
  );
}

export default observer(ServiceProviderEdit);
